import { useCallback, useEffect, useState } from "react";
import { navigateTo } from "@/App/navigateTo";

function currentPath(): string {
  return window.location.pathname;
}

/**
 * Tracks the current path and turns internal link clicks into client-side
 * navigations, so every `<a href="/...">` in the portal works without a full
 * page reload.
 *
 * Delegating one listener on the document — rather than wiring an onClick per
 * link — means links rendered deep inside tables and panels need to know
 * nothing about routing.
 */
export function useCarrierRouting(): string {
  const [path, setPath] = useState(currentPath);

  useEffect(() => {
    const onPopState = () => setPath(currentPath());
    window.addEventListener("popstate", onPopState);
    return () => window.removeEventListener("popstate", onPopState);
  }, []);

  const handleClick = useCallback((event: MouseEvent) => {
    // Leave modified and non-primary clicks alone so cmd/ctrl-click and
    // middle-click still open a new tab.
    if (
      event.metaKey ||
      event.ctrlKey ||
      event.shiftKey ||
      event.altKey ||
      event.button !== 0
    )
      return;

    const href =
      (event.target as HTMLElement).closest("a")?.getAttribute("href") ??
      undefined;
    // Only internal paths are ours. External URLs, hash anchors, and other
    // protocols (mailto:, tel:) must reach the browser untouched.
    if (href === undefined || !href.startsWith("/")) return;

    event.preventDefault();
    navigateTo(href);
  }, []);

  useEffect(() => {
    document.addEventListener("click", handleClick);
    return () => document.removeEventListener("click", handleClick);
  }, [handleClick]);

  return path;
}
